/**
 * Process conformance (order) — the "reordered" drift bucket, client-side.
 *
 * The server emits, model-free, the distinct ordered service-hop sequences it
 * observed (each with a trace count). Here — where the model lives — we project a
 * process's modeled steps to the SAME service-edge keys the board telemetry uses
 * ({@link trafficEdgeKey}), then compare each observed ordering against the
 * modeled order and flag the steps that ran out of order.
 *
 * Reorder is detected by PAIRWISE INVERSION, not LCS: for a modeled pair i<j
 * whose keys both appear observed, if the observed position of i is after j,
 * BOTH steps are flagged. (LCS would drop only one of an adjacent swap.) Only
 * significant sequential hops are compared — steps inside `parallel`/`each`
 * blocks (order not significant) and note/prose/`go` rows (no hop) are excluded,
 * and analysis is skipped unless the modeled keys are distinct and the observed
 * order is a permutation of them (a missing/extra step is a different bucket).
 */
import { trafficEdgeKey } from "../liveTrafficStore.js";

/** A modeled process step reduced to its normalized service-edge key. */
export interface ModeledStep {
  readonly stepIndex: number;
  readonly key: string;
}

/** The minimal walker-step shape we read (WalkStep is assignable). */
export interface StepLike {
  readonly stepIndex: number;
  readonly from: readonly string[];
  readonly to: readonly string[];
  readonly noteText?: string;
  readonly prose?: boolean;
  readonly reentryTarget?: string;
  readonly modifiers: { readonly inParallel: boolean; readonly inEach: boolean };
}

/** One distinct observed ordering: cross-service hops in order, + how many
 *  traces ran it. Keys are raw service names (matched via trafficEdgeKey). */
export interface ObservedOrdering {
  readonly seq: readonly (readonly [string, string])[];
  readonly count: number;
}

export interface ReorderResult {
  /** stepIndexes that ran out of order in at least one observed trace. */
  readonly reorderedSteps: ReadonlySet<number>;
  /** Traces whose order was a reordered permutation of the modeled steps. */
  readonly reorderedTraces: number;
  /** Traces that ran the modeled order exactly. */
  readonly conformingTraces: number;
}

const EMPTY: ReorderResult = { reorderedSteps: new Set(), reorderedTraces: 0, conformingTraces: 0 };

/** Project a process's walker steps to ordered service-edge keys, keeping only
 *  significant sequential hops (drops notes/prose/`go` and parallel/each steps). */
export function modeledStepSeq(steps: readonly StepLike[]): ModeledStep[] {
  const out: ModeledStep[] = [];
  for (const s of steps) {
    if (s.noteText || s.prose || s.reentryTarget) continue;
    if (s.modifiers.inParallel || s.modifiers.inEach) continue;
    const from = s.from[0];
    const to = s.to[0];
    if (!from || !to) continue;
    out.push({ stepIndex: s.stepIndex, key: trafficEdgeKey(from, to) });
  }
  return out;
}

/** Inverted step indices for one observed key ordering vs the modeled steps, or
 *  null when the observed order isn't a permutation of the modeled keys. */
function invertedFor(modeled: readonly ModeledStep[], obsKeys: readonly string[]): Set<number> | null {
  const modeledSet = new Set(modeled.map((m) => m.key));
  const filtered = obsKeys.filter((k) => modeledSet.has(k));
  // Must be a permutation: same length + same distinct set (no dup, none missing).
  if (filtered.length !== modeled.length || new Set(filtered).size !== modeledSet.size) return null;
  const pos = new Map<string, number>();
  filtered.forEach((k, i) => pos.set(k, i));
  const flagged = new Set<number>();
  for (let i = 0; i < modeled.length; i++) {
    for (let j = i + 1; j < modeled.length; j++) {
      if (pos.get(modeled[i].key)! > pos.get(modeled[j].key)!) {
        flagged.add(modeled[i].stepIndex);
        flagged.add(modeled[j].stepIndex);
      }
    }
  }
  return flagged;
}

/**
 * Compare every observed ordering against the modeled step order; union the
 * out-of-order steps and tally reordered vs conforming traces. Empty (no flags)
 * when the process has fewer than 2 significant steps, non-distinct keys, or no
 * observed ordering is a permutation of the modeled steps.
 */
export function analyzeReorder(
  modeled: readonly ModeledStep[],
  observed: readonly ObservedOrdering[],
): ReorderResult {
  if (modeled.length < 2) return EMPTY;
  const keys = modeled.map((m) => m.key);
  if (new Set(keys).size !== keys.length) return EMPTY; // non-distinct → can't align

  const flagged = new Set<number>();
  let reorderedTraces = 0;
  let conformingTraces = 0;
  for (const o of observed) {
    const obsKeys = o.seq.map(([f, t]) => trafficEdgeKey(f, t));
    const inv = invertedFor(modeled, obsKeys);
    if (inv === null) continue; // not this process's order (missing/extra step)
    if (inv.size > 0) {
      reorderedTraces += o.count;
      for (const s of inv) flagged.add(s);
    } else {
      conformingTraces += o.count;
    }
  }
  if (flagged.size === 0 && reorderedTraces === 0 && conformingTraces === 0) return EMPTY;
  return { reorderedSteps: flagged, reorderedTraces, conformingTraces };
}
